import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useGetPaymentStatistic } from 'src/features/statistic/api/useGetPaymentStatistic';
import DurationPicker from 'src/features/statistic/components/DurationPicker';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { CURRENCY } from 'src/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { tableMessages } from 'src/registry/components/table';
import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import ValidationsTableAccountDashboard from 'src/features/validations/components/tables/ValidationsTableAccountDashboard';
import { CURRENCY_MAP } from 'src/features/pricingPlan/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

enum Filter {
    CURRENCY = 'currency',
}

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';

const getDatetimeToday = () => {
    return {
        from_datetime: dayjs().set('hour', 0).set('minute', 0).format(DATE_FORMAT),
        to_datetime: dayjs().format(DATE_FORMAT),
    };
};

export default function ValidationStatistics() {
    const { formatMessage, formatNumber } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { parkingId } = useParking();

    const [filter, setFilter] = React.useState<Filter | null>(null);
    const [currency, setCurrency] = React.useState(CURRENCY.EUR);
    const [dateParams, setDateParams] = React.useState(getDatetimeToday);
    const { data, isLoading } = useGetPaymentStatistic({ ...dateParams, currency, parkingId });

    const paymentData = React.useMemo(() => {
        const total = data?.totals
            ? formatNumber(data.totals.total_amount / 100, {
                  style: 'currency',
                  currency,
              })
            : '-';

        const average = data?.totals
            ? formatNumber(data.totals.average_amount / 100, {
                  style: 'currency',
                  currency,
              })
            : '-';

        return [
            { title: formatMessage(statisticMessages.total), value: total },
            { title: formatMessage(statisticMessages.transactions), value: data?.totals.transactions_count ?? '-' },
            { title: formatMessage(statisticMessages.average), value: average },
            { title: formatMessage(statisticMessages.methods), value: data?.totals.methods_count ?? '-' },
            { title: formatMessage(statisticMessages.products), value: data?.totals.products_count ?? '-' },
            {
                title: formatMessage(statisticMessages.paymentMachines),
                value: data?.totals.payment_machines_count ?? '-',
            },
        ];
    }, [currency, data?.totals, formatMessage, formatNumber]);

    React.useEffect(() => {
        setTitle(formatMessage(statisticMessages.validations));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    return (
        <>
            <div className="flex justify-between">
                <div className="flex gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline">
                                <FontAwesomeIcon icon={filter ? ICON_MAP.FILTER_LIST : ICON_MAP.FILTER} />
                                {formatMessage(tableMessages.addFilter)}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuLabel>{formatMessage(statisticMessages.chooseFilter)}</DropdownMenuLabel>
                            {!filter && <DropdownMenuSeparator />}
                            <DropdownMenuCheckboxItem
                                checked={filter === Filter.CURRENCY}
                                onSelect={() => {
                                    setFilter((prevState) => (prevState ? null : Filter.CURRENCY));
                                    setCurrency(CURRENCY.EUR);
                                }}
                            >
                                {formatMessage(statisticMessages.currency)}
                            </DropdownMenuCheckboxItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItemWithIcon
                                variant="destructive"
                                icon={ICON_MAP.DELETE}
                                onSelect={() => {
                                    setFilter(null);
                                    setCurrency(CURRENCY.EUR);
                                }}
                            >
                                {formatMessage(tableMessages.clearFilter)}
                            </DropdownMenuItemWithIcon>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {filter === Filter.CURRENCY && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline">
                                    <FontAwesomeIcon
                                        icon={ICON_MAP.FILTER_LIST}
                                        className="text-primary-700 xs:block hidden"
                                    />
                                    <span className="flex flex-col items-start sm:ml-1">
                                        <span className="-mb-1 text-xs font-normal opacity-70">
                                            {formatMessage(statisticMessages.currency)}:
                                        </span>
                                        <span className="text-sm">{formatMessage(CURRENCY_MAP[currency])}</span>
                                    </span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuLabel>{formatMessage(statisticMessages.chooseCurrency)}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuCheckboxItem
                                    checked={currency === CURRENCY.EUR}
                                    onSelect={() => setCurrency(CURRENCY.EUR)}
                                >
                                    {formatMessage(CURRENCY_MAP[CURRENCY.EUR])}
                                </DropdownMenuCheckboxItem>
                                <DropdownMenuCheckboxItem
                                    checked={currency === CURRENCY.BGN}
                                    onSelect={() => setCurrency(CURRENCY.BGN)}
                                >
                                    {formatMessage(CURRENCY_MAP[CURRENCY.BGN])}
                                </DropdownMenuCheckboxItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItemWithIcon
                                    variant="destructive"
                                    icon={ICON_MAP.DELETE}
                                    onSelect={() => {
                                        setFilter(null);
                                        setCurrency(CURRENCY.EUR);
                                    }}
                                >
                                    {formatMessage(tableMessages.clearFilter)}
                                </DropdownMenuItemWithIcon>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
                <DurationPicker dateParams={dateParams} setDateParams={setDateParams} />
            </div>
            <div className="flex flex-1 flex-col gap-6">
                <GeneralBlock className="grid grid-cols-6">
                    {paymentData.map(({ title, value }, i) => (
                        <div key={i} className="flex flex-col">
                            <span className="text-sm text-gray-600">{title}</span>
                            {isLoading ? (
                                <Skeleton className="h-8 w-24" />
                            ) : (
                                <span className="text-2xl font-semibold">{value}</span>
                            )}
                        </div>
                    ))}
                </GeneralBlock>
                <ValidationsTableAccountDashboard />
            </div>
        </>
    );
}
